import { generatePayFastSignature } from './payfastSignature';

/** Increment when deploying — shown on PayFast checkout to confirm live build. */
export const APP_BUILD_ID = '20250609-payfast-v7';

/**
 * PayFast rejects payments where the buyer is the merchant account holder
 * ("you cannot pay yourself"). Set REACT_APP_ADMIN_PAYFAST_ACCOUNT_EMAIL so we
 * can warn the user up front instead of failing on PayFast's side.
 */
export function isMerchantSelfPayment(buyerEmail: string): boolean {
  const merchantEmail = process.env.REACT_APP_ADMIN_PAYFAST_ACCOUNT_EMAIL;
  if (!merchantEmail) return false;
  return buyerEmail.trim().toLowerCase() === merchantEmail.trim().toLowerCase();
}

export const MERCHANT_SELF_PAYMENT_MESSAGE =
  'This email address belongs to the PayFast merchant account. PayFast does not allow paying yourself — please register with a different email address.';

/** Field order for PayFast checkout form posts (matches PayFast docs). */
export const PAYFAST_FORM_FIELD_ORDER = [
  'merchant_id',
  'merchant_key',
  'return_url',
  'cancel_url',
  'notify_url',
  'name_first',
  'name_last',
  'email_address',
  'm_payment_id',
  'amount',
  'item_name',
] as const;

export interface PayFastCheckoutInput {
  merchant_id: string;
  merchant_key: string;
  passphrase?: string;
  return_url: string;
  cancel_url: string;
  notify_url: string;
  name_first: string;
  name_last: string;
  email_address: string;
  m_payment_id: string;
  amount: string;
  item_name: string;
}

export interface PayFastCheckoutResult {
  action: string;
  fields: Record<string, string>;
  signature: string;
}

export function getPayFastSiteOrigin(): string {
  if (typeof window !== 'undefined' && window.location.origin) {
    return window.location.origin;
  }
  return process.env.REACT_APP_SITE_URL || 'https://register.gomtb.co.za';
}

/** PayFast callback URLs for registration payments. */
export function getPayFastCallbackUrls(siteOrigin: string): {
  return_url: string;
  cancel_url: string;
  notify_url: string;
} {
  return {
    return_url:
      process.env.REACT_APP_PAYFAST_RETURN_URL || `${siteOrigin}/payment/success`,
    cancel_url:
      process.env.REACT_APP_PAYFAST_CANCEL_URL || `${siteOrigin}/payment/cancel`,
    notify_url:
      process.env.REACT_APP_PAYFAST_NOTIFY_URL || `${siteOrigin}/payment/notify`,
  };
}

export function getPayFastProcessUrl(useProduction = true): string {
  const environment = process.env.REACT_APP_PAYFAST_ENVIRONMENT || 'production';
  const productionUrl =
    process.env.REACT_APP_PAYFAST_PRODUCTION_URL || 'https://www.payfast.co.za/eng/process';
  const sandboxUrl =
    process.env.REACT_APP_PAYFAST_SANDBOX_URL || 'https://sandbox.payfast.co.za/eng/process';

  if (useProduction || environment === 'production') {
    return productionUrl;
  }
  return sandboxUrl;
}

export function buildPayFastCheckout(input: PayFastCheckoutInput): PayFastCheckoutResult {
  const paymentData: Record<string, string> = {
    merchant_id: input.merchant_id.trim(),
    merchant_key: input.merchant_key.trim(),
    return_url: input.return_url.trim(),
    cancel_url: input.cancel_url.trim(),
    notify_url: input.notify_url.trim(),
    name_first: input.name_first.trim(),
    name_last: input.name_last.trim(),
    email_address: input.email_address.trim(),
    m_payment_id: input.m_payment_id.trim(),
    amount: input.amount.trim(),
    item_name: input.item_name.trim(),
  };

  const signature = generatePayFastSignature(paymentData, input.passphrase);

  return {
    action: getPayFastProcessUrl(true),
    signature,
    fields: {
      ...paymentData,
      signature,
    },
  };
}

export function orderedPayFastFieldEntries(
  fields: Record<string, string>
): [string, string][] {
  const entries: [string, string][] = [];

  PAYFAST_FORM_FIELD_ORDER.forEach((key) => {
    if (fields[key] != null && fields[key] !== '') {
      entries.push([key, fields[key]]);
    }
  });

  if (fields.signature) {
    entries.push(['signature', fields.signature]);
  }

  return entries;
}

const CHECKOUT_STORAGE_KEY = 'payfast_checkout_pending';

export interface StoredPayFastCheckout {
  action: string;
  fields: Record<string, string>;
  passphrase?: string;
  buildId?: string;
}

export function storePayFastCheckout(checkout: StoredPayFastCheckout): void {
  sessionStorage.setItem(CHECKOUT_STORAGE_KEY, JSON.stringify(checkout));
}

export function readPayFastCheckout(): StoredPayFastCheckout | null {
  try {
    const raw = sessionStorage.getItem(CHECKOUT_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as StoredPayFastCheckout;
  } catch {
    return null;
  }
}

export function clearPayFastCheckout(): void {
  sessionStorage.removeItem(CHECKOUT_STORAGE_KEY);
}
